import { AnimatePresence, m } from 'motion/react';
import { Languages, Monitor, Moon, Sun } from 'lucide-react';

import { useI18n, useT } from '../i18n/provider';
import { LOCALES, LOCALE_NAMES } from '../i18n/locale';
import type { Locale } from '../i18n/locale';
import { auth } from '../api/endpoints';
import { useAuth } from '../stores/auth';
import { useTheme } from '../stores/theme';
import type { ThemeChoice } from '../stores/theme';

import { Menu, MenuItem, MenuLabel } from './Menu';

/**
 * El interruptor de tema de la cabecera.
 *
 * Un solo botón que alterna entre claro y oscuro, con el icono girando y
 * fundiéndose; la tercera opción, «sistema», vive en el menú de cuenta, donde
 * está quien la busca a propósito. Poner las tres aquí obligaría a un menú para
 * el gesto más frecuente de todos.
 */
export function ThemeToggle() {
  const t = useT();
  const resolved = useTheme((state) => state.resolved);
  const toggle = useTheme((state) => state.toggle);
  const dark = resolved === 'dark';

  return (
    <button
      type="button"
      onClick={toggle}
      title={dark ? t('theme.toggleToLight') : t('theme.toggleToDark')}
      aria-label={dark ? t('theme.toggleToLight') : t('theme.toggleToDark')}
      className="relative grid size-9 place-items-center rounded-sm text-ink-soft transition-colors duration-fast hover:bg-muted hover:text-ink"
    >
      <AnimatePresence initial={false} mode="wait">
        <m.span
          key={resolved}
          initial={{ opacity: 0, rotate: -75, scale: 0.6 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 75, scale: 0.6 }}
          transition={{ duration: 0.2, ease: [0.22, 0.8, 0.28, 1] }}
          className="grid place-items-center"
        >
          {dark ? <Moon className="size-4.5" aria-hidden="true" /> : <Sun className="size-4.5" aria-hidden="true" />}
        </m.span>
      </AnimatePresence>
    </button>
  );
}

/** Las tres opciones completas, para el menú de cuenta y la pantalla de ajustes. */
export function ThemeChoices() {
  const t = useT();
  const choice = useTheme((state) => state.choice);
  const setChoice = useTheme((state) => state.setChoice);

  const options: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: t('theme.light'), icon: Sun },
    { value: 'dark', label: t('theme.dark'), icon: Moon },
    { value: 'system', label: t('theme.system'), icon: Monitor },
  ];

  return (
    <div role="radiogroup" aria-label={t('theme.label')} className="flex gap-1 rounded-sm bg-muted p-1">
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={choice === value}
          onClick={() => setChoice(value)}
          className="relative flex flex-1 items-center justify-center gap-1.5 rounded-xs px-2 py-1.5 text-xs font-semibold"
        >
          {/* El fondo se desliza de una opción a otra en vez de aparecer:
              `layoutId` es lo que hace que se lea como un mismo objeto. */}
          {choice === value ? (
            <m.span
              layoutId="theme-choice"
              className="absolute inset-0 rounded-xs bg-raised shadow-xs"
              transition={{ type: 'spring', stiffness: 480, damping: 38 }}
            />
          ) : null}
          <span className={choice === value ? 'relative text-ink' : 'relative text-ink-faint'}>
            <Icon className="size-3.5" aria-hidden="true" />
          </span>
          <span className={choice === value ? 'relative text-ink' : 'relative text-ink-faint'}>{label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * El selector de idioma.
 *
 * Cuando hay sesión, guarda la preferencia con `PATCH /auth/me` —el mismo
 * endpoint que la pantalla de cuenta— para que viaje con el usuario a
 * cualquier navegador. Sin sesión, en el login o en el reseteo de contraseña,
 * no hay a quién guardársela y se queda en el espejo local; que el cambio se
 * aplique igual es justo el motivo de que ese espejo exista.
 *
 * Si el `PATCH` falla, el idioma ya ha cambiado en pantalla y se queda así: es
 * una preferencia, no un dato, y revertirlo delante del usuario sería peor que
 * volver a intentarlo la próxima vez.
 */
export function LanguagePicker({ compact = false }: { compact?: boolean }) {
  const t = useT();
  const { locale, setLocale } = useI18n();

  const choose = (next: Locale) => {
    setLocale(next);

    if (useAuth.getState().accessToken) {
      void auth.updateProfile({ locale: next }).then(
        (user) => useAuth.getState().setUser(user),
        () => undefined,
      );
    }
  };

  return (
    <Menu
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={t('language.change')}
          title={t('language.change')}
          className="flex h-9 items-center gap-1.5 rounded-sm px-2 text-ink-soft transition-colors duration-fast hover:bg-muted hover:text-ink"
        >
          <Languages className="size-4.5" aria-hidden="true" />
          {compact ? null : <span className="text-xs font-bold uppercase">{locale}</span>}
        </button>
      )}
    >
      {({ close }) => (
        <>
          <MenuLabel>{t('language.interface')}</MenuLabel>
          {LOCALES.map((value) => (
            <MenuItem
              key={value}
              active={locale === value}
              onClick={() => {
                choose(value);
                close();
              }}
            >
              {LOCALE_NAMES[value]}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
}
